// Public-site consent UI. Necessary storage is always available; public marketing analytics is
// strictly opt-in and is controlled through src/lib/cookieConsent.js + src/lib/analytics.js.
import React, { useEffect, useId, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { BarChart3, Check, LockKeyhole, ShieldCheck } from "lucide-react";
import { Button } from "./ui/primitives";
import { CUSTOM_MODAL_LAYER_EVENT, Modal } from "./ui/Modal";
import {
  COOKIE_CONSENT_EVENT,
  COOKIE_CONSENT_KEY,
  COOKIE_PREFERENCES_EVENT,
  readCookieConsent,
  saveCookieConsent,
} from "../lib/cookieConsent";

function PreferenceRow({ icon: Icon, title, description, state, control }) {
  return (
    <div className="flex items-start gap-3 rounded-2xl border border-line bg-surface p-4 shadow-[0_1px_0_rgba(15,23,42,0.03)]">
      <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full border border-primary/15 bg-primary/10 text-primary" aria-hidden="true">
        <Icon className="h-4 w-4" />
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="font-heading text-sm font-semibold text-ink">{title}</p>
          {state}
        </div>
        <p className="mt-1 text-[13px] leading-relaxed text-ink-2">{description}</p>
      </div>
      {control}
    </div>
  );
}

// UX-09: how far the visitor may read before the undecided banner condenses to the compact
// "Cookies" pill (the DESIGN.md scroll-dock contract). The decision stays strictly explicit —
// analytics remains OFF until a real choice — the pill only shrinks the banner's footprint so
// it stops covering page content while someone reads.
const DOCK_SCROLL_PX = 900;

export default function CookieConsent() {
  const [consent, setConsent] = useState(readCookieConsent);
  const [preferencesOpen, setPreferencesOpen] = useState(false);
  const [analyticsEnabled, setAnalyticsEnabled] = useState(consent?.analytics === true);
  const [customModalOpen, setCustomModalOpen] = useState(() => (
    typeof document !== "undefined" && Boolean(document.querySelector('[data-modal-layer="true"]'))
  ));
  const [docked, setDocked] = useState(() => typeof window !== "undefined" && window.scrollY > DOCK_SCROLL_PX);
  const [pillExpanded, setPillExpanded] = useState(false); // explicit re-expand from the pill wins until decided
  const analyticsId = useId();
  const analyticsRef = useRef(null);
  const manageButtonRef = useRef(null);
  const acceptButtonRef = useRef(null);
  const returnToBannerRef = useRef(false);

  useEffect(() => {
    const openPreferences = () => {
      const current = readCookieConsent();
      setConsent(current);
      setAnalyticsEnabled(current?.analytics === true);
      setPreferencesOpen(true);
    };
    const syncConsent = () => {
      const next = readCookieConsent();
      setConsent(next);
      if (next) setAnalyticsEnabled(next.analytics === true);
    };
    window.addEventListener(COOKIE_PREFERENCES_EVENT, openPreferences);
    window.addEventListener(COOKIE_CONSENT_EVENT, syncConsent);
    window.addEventListener("storage", syncConsent);
    return () => {
      window.removeEventListener(COOKIE_PREFERENCES_EVENT, openPreferences);
      window.removeEventListener(COOKIE_CONSENT_EVENT, syncConsent);
      window.removeEventListener("storage", syncConsent);
    };
  }, []);

  useEffect(() => {
    // A modal portal sits outside #root while the rest of the application becomes inert. The
    // consent notice lives inside #root, but its fixed z-index used to remain visually above a
    // dialog. Remove the notice for the complete modal lifecycle so it is neither visible nor a
    // competing focus/pointer target, then restore the still-undecided choice after close.
    const syncModalLayers = (event) => {
      const reportedCount = event?.detail?.count;
      setCustomModalOpen(
        Number.isFinite(reportedCount)
          ? reportedCount > 0
          : Boolean(document.querySelector('[data-modal-layer="true"]')),
      );
    };
    syncModalLayers();
    document.addEventListener(CUSTOM_MODAL_LAYER_EVENT, syncModalLayers);
    return () => document.removeEventListener(CUSTOM_MODAL_LAYER_EVENT, syncModalLayers);
  }, []);

  useEffect(() => {
    if (preferencesOpen || consent || customModalOpen || !returnToBannerRef.current) return;
    returnToBannerRef.current = false;
    manageButtonRef.current?.focus({ preventScroll: true });
  }, [preferencesOpen, consent, customModalOpen]);

  // UX-09: scroll-dock while undecided. Passive listener; a decision removes both surfaces, so
  // the listener only matters pre-decision. Scrolling back above the threshold restores the full
  // banner (and clears any manual re-expand, keeping the state machine one-directional per zone).
  useEffect(() => {
    if (consent) return undefined;
    const onScroll = () => {
      const next = window.scrollY > DOCK_SCROLL_PX;
      setDocked(next);
      if (!next) setPillExpanded(false); // leaving the docked zone resets the manual override
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [consent]);

  const decide = (analytics) => {
    const next = saveCookieConsent({ analytics });
    setConsent(next);
    setAnalyticsEnabled(next.analytics);
    returnToBannerRef.current = false;
    setPreferencesOpen(false);
  };

  const closePreferences = () => {
    returnToBannerRef.current = !consent;
    setPreferencesOpen(false);
  };
  const showBanner = !consent && !preferencesOpen && !customModalOpen;
  // UX-09: past the dock threshold the undecided banner condenses to the "Cookies" pill unless
  // the visitor explicitly re-expanded it. Both surfaces share the `cookie-consent` testid — the
  // AiraAssistant clearance measurement and the mobile-nav sheet clamp track whichever renders.
  const showFullBanner = showBanner && (!docked || pillExpanded);
  const showPill = showBanner && docked && !pillExpanded;

  const expandFromPill = () => {
    setPillExpanded(true);
    window.requestAnimationFrame(() => acceptButtonRef.current?.focus({ preventScroll: true }));
  };

  return (
    <>
      {showPill && (
        <section aria-label="Cookie settings" data-testid="cookie-consent" className="fixed bottom-[max(0.75rem,env(safe-area-inset-bottom))] left-3 z-[80] sm:left-5">
          <button
            type="button"
            onClick={expandFromPill}
            data-testid="cookie-consent-pill"
            aria-expanded={false}
            className="inline-flex min-h-11 items-center gap-2 rounded-full border border-sky-300/25 bg-[#06152C]/[0.97] px-4 text-[13px] font-semibold text-white shadow-[0_16px_40px_-20px_rgba(1,8,25,0.9)] backdrop-blur-xl transition-colors duration-150 hover:border-sky-300/50 focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-300"
          >
            <ShieldCheck className="h-4 w-4 text-sky-300" aria-hidden="true" /> Cookies
          </button>
        </section>
      )}
      {showFullBanner && (
        <section
          aria-labelledby="cookie-consent-title"
          aria-describedby="cookie-consent-description"
          data-testid="cookie-consent"
          className="fixed inset-x-3 bottom-[max(0.75rem,env(safe-area-inset-bottom))] z-[80] mx-auto max-w-6xl overflow-hidden rounded-[1.25rem] border border-sky-300/20 bg-[#06152C]/[0.97] text-white shadow-[0_24px_70px_-30px_rgba(1,8,25,0.9)] backdrop-blur-xl motion-safe:animate-modal-in motion-reduce:animate-none sm:inset-x-5"
        >
          <div className="h-px bg-gradient-to-r from-transparent via-sky-300/70 to-transparent" aria-hidden="true" />
          <div className="grid gap-4 p-4 sm:p-5 xl:grid-cols-[minmax(0,1fr)_auto] xl:items-center">
            <div className="flex items-start gap-3.5">
              <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full border border-sky-300/25 bg-sky-300/10 text-sky-300" aria-hidden="true">
                <ShieldCheck className="h-5 w-5" />
              </span>
              <div className="min-w-0">
                <h2 id="cookie-consent-title" className="font-heading text-base font-semibold tracking-tight text-white">
                  You control analytics.
                </h2>
                <p id="cookie-consent-description" className="mt-1 max-w-3xl text-[13px] leading-relaxed text-white/[0.68] sm:text-sm">
                  Necessary storage keeps sign-in and saved preferences working. Optional analytics and session replay stay off until you allow them, and our event contract excludes names, contact details and student records.{" "}
                  <Link to="/cookie-policy" className="rounded font-semibold text-sky-300 underline decoration-sky-300/45 underline-offset-4 transition-colors hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-300">
                    Cookie Policy
                  </Link>
                </p>
              </div>
            </div>
            {/* UX-09 footprint budget: two columns below `sm` (Manage spans both) keeps the whole
                undecided banner ≈ 260px tall at 390×844 — under the 35%-of-viewport (295px) cap. */}
            <div role="group" className="grid grid-cols-2 gap-2 sm:grid-cols-3 xl:w-[31rem]" aria-label="Cookie consent choices">
              <button
                ref={acceptButtonRef}
                type="button"
                onClick={() => decide(true)}
                data-testid="cookie-consent-accept"
                className="inline-flex min-h-11 items-center justify-center rounded-full bg-[#2563EB] px-4 text-[13px] font-semibold text-white shadow-[0_8px_24px_-12px_rgba(59,130,246,0.9)] transition-colors duration-150 hover:bg-[#1D4ED8] focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-300 focus-visible:ring-offset-2 focus-visible:ring-offset-[#06152C]"
              >
                Accept optional
              </button>
              <button
                type="button"
                onClick={() => decide(false)}
                data-testid="cookie-consent-necessary"
                className="inline-flex min-h-11 items-center justify-center rounded-full border border-white/20 bg-white/[0.055] px-4 text-[13px] font-semibold text-white transition-[background-color,border-color] duration-150 hover:border-white/35 hover:bg-white/[0.1] focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-300 focus-visible:ring-offset-2 focus-visible:ring-offset-[#06152C]"
              >
                Necessary only
              </button>
              <button
                ref={manageButtonRef}
                type="button"
                onClick={() => {
                  returnToBannerRef.current = true;
                  setAnalyticsEnabled(false);
                  setPreferencesOpen(true);
                }}
                data-testid="cookie-consent-manage"
                className="col-span-2 inline-flex min-h-11 items-center justify-center rounded-full px-4 text-[13px] font-semibold text-white/[0.75] transition-[background-color,color] duration-150 hover:bg-white/[0.07] hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-300 focus-visible:ring-offset-2 focus-visible:ring-offset-[#06152C] sm:col-span-1"
              >
                Manage preferences
              </button>
            </div>
          </div>
        </section>
      )}

      <Modal
        open={preferencesOpen}
        onClose={closePreferences}
        title="Cookie preferences"
        description="Choose whether optional analytics may run on public pages. Necessary storage remains active."
        size="md"
        initialFocusRef={analyticsRef}
        layerClassName="z-[90]"
        footer={(
          <>
            <Button variant="outline" className="motion-reduce:transform-none" onClick={closePreferences}>Cancel</Button>
            <Button className="motion-reduce:transform-none" onClick={() => decide(analyticsEnabled)} data-testid="cookie-preferences-save">Save preferences</Button>
          </>
        )}
      >
        <div className="space-y-3" data-testid="cookie-preferences-dialog">
          <p className="text-sm leading-relaxed text-ink-2">
            Analytics are off unless you allow them. You can revisit this choice at any time from <span className="font-semibold text-ink">Cookie settings</span> in the footer.
          </p>
          <PreferenceRow
            icon={LockKeyhole}
            title="Necessary"
            description="Supports secure sign-in, security protections, your consent choice and core device preferences."
            state={(
              <span className="inline-flex items-center gap-1 rounded-full border border-positive/20 bg-positive-soft px-2 py-1 text-xs font-semibold text-positive">
                <Check className="h-3.5 w-3.5" aria-hidden="true" /> Always active
              </span>
            )}
          />
          <label htmlFor={analyticsId} className="block cursor-pointer rounded-2xl focus-within:ring-2 focus-within:ring-primary/45 focus-within:ring-offset-2">
            <PreferenceRow
              icon={BarChart3}
              title="Optional analytics"
              description="If enabled, Google Analytics receives public-page and interaction events, and Microsoft Clarity records anonymised session replays and heatmaps of this marketing site. Advertising signals and ad personalisation stay disabled."
              state={(
                <span className="text-xs font-semibold text-ink-subtle">
                  {analyticsEnabled ? "Allowed" : "Not allowed"}
                </span>
              )}
              control={(
                <span className={`relative mt-0.5 inline-flex h-7 w-12 shrink-0 items-center rounded-full p-0.5 transition-[background-color] duration-150 ${analyticsEnabled ? "bg-primary" : "bg-line"}`} aria-hidden="true">
                  <span className={`h-6 w-6 rounded-full bg-white shadow-sm transition-transform duration-150 motion-reduce:transition-none ${analyticsEnabled ? "translate-x-5" : "translate-x-0"}`} />
                </span>
              )}
            />
            <input
              ref={analyticsRef}
              id={analyticsId}
              type="checkbox"
              checked={analyticsEnabled}
              onChange={(event) => setAnalyticsEnabled(event.target.checked)}
              className="sr-only"
              data-testid="cookie-analytics-toggle"
            />
          </label>
          <p className="text-xs leading-relaxed text-ink-subtle">
            Choosing “Necessary only” does not affect signing in or any product feature you use. This choice covers this marketing site only.
          </p>
          <Link to="/cookie-policy" className="inline-flex min-h-11 items-center rounded-lg text-sm font-semibold text-accent hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-primary">
            View the full Cookie Policy
          </Link>
        </div>
      </Modal>
    </>
  );
}

export { COOKIE_CONSENT_KEY };
